import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customer_token'));
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (jwt) => {
    try {
      const response = await api.get('/api/customer/profile', {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      setCustomer(response.data);
    } catch {
      localStorage.removeItem('customer_token');
      setToken(null);
      setCustomer(null);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchProfile(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, fetchProfile]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/customer/login', { email, password });
      const { token: newToken } = response.data;
      setToken(newToken);
      localStorage.setItem('customer_token', newToken);
      await fetchProfile(newToken);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (name, email, password, phone, securityFields = {}) => {
    try {
      const response = await api.post('/api/customer/register', {
        name, email, password, phone,
        ...securityFields,
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customer_token');
  };

  const forgotPassword = async (email) => {
    try {
      const response = await api.post('/api/customer/forgot-password', { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Something went wrong'
      };
    }
  };

  const resetPassword = async (resetToken, password) => {
    try {
      const response = await api.post('/api/customer/reset-password', { token: resetToken, password });
      // Auto-login: if the backend returned a JWT, store it and fetch the profile
      // so the user lands directly on /account without a second login step.
      const jwt = response.data.token;
      if (jwt) {
        setToken(jwt);
        localStorage.setItem('customer_token', jwt);
        await fetchProfile(jwt);
      }
      return { success: true, message: response.data.message, token: jwt || null };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Something went wrong'
      };
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await api.put('/api/customer/profile', data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Update failed'
      };
    }
  };

  const value = {
    customer,
    token,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    isAuthenticated: !!token && !!customer,
    loading
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
